import type { UiError } from "../../errors/uiError";
import { parseProblemDetails, PROBLEM_JSON_MEDIA_TYPE, type ProblemDetails } from "./problemDetails";

/**
 * The one place that turns a failed HTTP response into a typed `UiError`. A body in the
 * problem details form becomes a `problem` error keyed by its code. A gateway error without
 * one (502, 503, 504) means a proxy answered because the server did not: `serverUnreachable`.
 * Anything else becomes an `unexpectedResponse`. Response text is never matched, and never
 * shown as the message.
 */
export async function uiErrorFromFailedResponse(response: Response): Promise<UiError> {
  const bodyText = await response.text();
  return uiErrorFromFailedResponseBody(
    response.status,
    response.headers.get("content-type"),
    bodyText,
  );
}

export function uiErrorFromFailedResponseBody(
  status: number,
  contentType: string | null,
  bodyText: string,
): UiError {
  const problem = isJsonMediaType(contentType) ? parseJsonProblem(bodyText) : null;
  if (problem === INVALID_JSON) {
    return { kind: "unexpectedResponse", status, reason: "invalidJson" };
  }
  if (problem !== null) {
    return { kind: "problem", problem };
  }
  if (GATEWAY_ERROR_STATUSES.has(status)) {
    return { kind: "serverUnreachable" };
  }
  return { kind: "unexpectedResponse", status, reason: "notProblemDetails" };
}

/** Bad gateway, service unavailable, gateway timeout: what a proxy answers for a server. */
const GATEWAY_ERROR_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

const INVALID_JSON = Symbol("invalid JSON");

function parseJsonProblem(bodyText: string): ProblemDetails | null | typeof INVALID_JSON {
  let body: unknown;
  try {
    body = JSON.parse(bodyText);
  } catch {
    return INVALID_JSON;
  }
  return parseProblemDetails(body);
}

function isJsonMediaType(contentType: string | null): boolean {
  const mediaType = contentType?.split(";")[0]?.trim().toLowerCase();
  return mediaType === PROBLEM_JSON_MEDIA_TYPE || mediaType === "application/json";
}
